import type { Eta } from "@veyso/core";
import { JOB_ACTIVITY_LABELS, type ActiveJob } from "./jobActivity.js";

/** Never the raw type: a kind nobody has words for is "Working". */
export function jobWords(job: Pick<ActiveJob, "label" | "type">): string {
  return job.label ?? JOB_ACTIVITY_LABELS[job.type] ?? "Working";
}

/** As `etaShort` reads it: from 55 minutes on, an hour. */
function shortMinutes(seconds: number): string {
  if (seconds < 60) return "<1 min";
  const m = Math.ceil(seconds / 60);
  return m >= 55 ? "~1 hr" : `${m} min`;
}

/** A usual duration, which is a guess already; "~1 hr" is not made "~~1 hr". */
function about(seconds: number): string {
  const words = shortMinutes(seconds);
  return words.startsWith("~") ? words : `~${words}`;
}

export function chipText(jobs: { label: string; eta: Eta | null }[]): string {
  if (jobs.length === 1) {
    const [only] = jobs;
    const eta = only.eta;
    if (eta?.kind === "estimate") return `${only.label} · ${shortMinutes(eta.seconds)} left`;
    if (eta?.kind === "typical") return `${only.label} · ${about(eta.seconds)}`;
    return only.label;
  }
  const soonest = jobs
    .flatMap((job) => (job.eta && (job.eta.kind === "estimate" || job.eta.kind === "typical") ? [job.eta.seconds] : []))
    .sort((a, b) => a - b)[0];
  return soonest === undefined
    ? `${jobs.length} in progress`
    : `${jobs.length} in progress · next in ${shortMinutes(soonest)}`;
}

/** The menu's last line, only when finishing one of these will email the person looking. */
export function menuNote(jobs: Pick<ActiveJob, "emailsYou">[]): string | null {
  return jobs.some((job) => job.emailsYou === true)
    ? "You can close Veyso. We'll email you as each one is ready."
    : null;
}

export function jobHref(job: Pick<ActiveJob, "projectId" | "type">): string {
  return job.type === "render" ? `/projects/${job.projectId}/render` : `/projects/${job.projectId}`;
}
